/* Nibby scratch & reveal popup: vanilla JS custom element. */
if (!customElements.get('nibby-scratch-popup')) {
  customElements.define(
    'nibby-scratch-popup',
    class NibbyScratchPopup extends HTMLElement {
      connectedCallback() {
        this.storageKey = 'nibbyScratchPopup';
        this.dialog = this.querySelector('[role="dialog"]');
        this.canvas = this.querySelector('[data-canvas]');
        this.card = this.querySelector('[data-card]');
        this.revealed = false;

        this.querySelectorAll('[data-close]').forEach((el) => el.addEventListener('click', () => this.close()));
        this.querySelector('[data-reveal]').addEventListener('click', () => this.reveal());
        this.querySelector('[data-form]').addEventListener('submit', (e) => this.submit(e));
        this.addEventListener('keydown', (e) => {
          if (e.key === 'Escape') this.close();
          if (e.key === 'Tab') this.trapFocus(e);
        });

        if (this.hasAttribute('data-preview')) {
          this.open();
          return;
        }
        this.teaser = this.querySelector('[data-teaser]');
        if (this.teaser) this.teaser.addEventListener('click', () => this.open());
        const state = this.read();
        if (state.subscribed) return;
        if (!this.shouldShow()) {
          if (state.closedAt) this.showTeaser();
          return;
        }

        const delay = Math.max(0, parseInt(this.dataset.delay, 10) || 0) * 1000;
        this.timer = setTimeout(() => this.open(), delay);
        if (this.dataset.exitIntent === 'true' && window.matchMedia('(pointer: fine)').matches) {
          this.onExit = (e) => {
            if (e.clientY <= 0) this.open();
          };
          document.addEventListener('mouseout', this.onExit);
        }
      }

      read() {
        try {
          return JSON.parse(localStorage.getItem(this.storageKey)) || {};
        } catch (e) {
          return {};
        }
      }

      write(data) {
        try {
          localStorage.setItem(this.storageKey, JSON.stringify(Object.assign(this.read(), data)));
        } catch (e) {
          /* storage blocked: popup simply shows again next visit */
        }
      }

      shouldShow() {
        const state = this.read();
        if (state.subscribed) return false;
        const days = parseInt(this.dataset.frequencyDays, 10) || 7;
        return !state.closedAt || Date.now() - state.closedAt > days * 864e5;
      }

      showTeaser() {
        if (!this.teaser || this.dataset.teaserEnabled === 'false') return;
        this.classList.add('is-teaser');
        this.hidden = false;
        this.teaser.hidden = false;
      }

      open() {
        if (this.isOpen) return;
        if (document.querySelector('.drawer.active, details[open] > .menu-drawer')) {
          // Do not interrupt an open cart or menu; try again a bit later.
          clearTimeout(this.timer);
          this.timer = setTimeout(() => this.open(), 5000);
          return;
        }
        this.isOpen = true;
        clearTimeout(this.timer);
        if (this.onExit) document.removeEventListener('mouseout', this.onExit);
        this.lastFocus = document.activeElement;
        this.classList.remove('is-teaser');
        if (this.teaser) this.teaser.hidden = true;
        this.hidden = false;
        document.documentElement.classList.add('nibby-scratch-open');
        requestAnimationFrame(() => {
          this.setupCanvas();
          this.dialog.focus();
        });
      }

      close() {
        if (!this.isOpen) return;
        this.isOpen = false;
        this.hidden = true;
        document.documentElement.classList.remove('nibby-scratch-open');
        if (!this.hasAttribute('data-preview')) this.write({ closedAt: Date.now() });
        if (!this.read().subscribed) this.showTeaser();
        if (this.lastFocus && this.lastFocus.focus) this.lastFocus.focus();
      }

      trapFocus(e) {
        const items = Array.from(
          this.dialog.querySelectorAll('button, a[href], input, [tabindex]:not([tabindex="-1"])')
        ).filter((el) => el.offsetParent !== null);
        if (!items.length) return;
        const first = items[0];
        const last = items[items.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }

      setupCanvas() {
        if (this.ctx || this.revealed) return;
        const rect = this.card.getBoundingClientRect();
        const ratio = window.devicePixelRatio || 1;
        this.canvas.width = Math.round(rect.width * ratio);
        this.canvas.height = Math.round(rect.height * ratio);
        const ctx = this.canvas.getContext('2d');
        ctx.scale(ratio, ratio);
        this.ctx = ctx;
        this.w = rect.width;
        this.h = rect.height;
        this.drawCoat();

        let drawing = false;
        let last = null;
        let moves = 0;
        const point = (e) => {
          const r = this.canvas.getBoundingClientRect();
          return { x: e.clientX - r.left, y: e.clientY - r.top };
        };
        this.canvas.addEventListener('pointerdown', (e) => {
          drawing = true;
          this.classList.add('is-scratching');
          last = point(e);
          this.canvas.setPointerCapture(e.pointerId);
          this.scratch(last, last);
        });
        this.canvas.addEventListener('pointermove', (e) => {
          if (!drawing) return;
          const p = point(e);
          this.scratch(last, p);
          last = p;
          if (++moves % 12 === 0 && this.clearedRatio() > 0.5) this.reveal();
        });
        const stop = () => {
          drawing = false;
          if (!this.revealed && this.clearedRatio() > 0.5) this.reveal();
        };
        this.canvas.addEventListener('pointerup', stop);
        this.canvas.addEventListener('pointercancel', stop);
      }

      drawCoat() {
        const { ctx, w, h } = this;
        const grad = ctx.createLinearGradient(0, 0, w, h);
        grad.addColorStop(0, '#F9D98A');
        grad.addColorStop(0.35, '#F2B544');
        grad.addColorStop(0.55, '#FBE3A6');
        grad.addColorStop(0.75, '#F2B544');
        grad.addColorStop(1, '#DE9C2C');
        ctx.fillStyle = grad;
        ctx.fillRect(0, 0, w, h);
        // fine foil noise
        for (let i = 0; i < (w * h) / 40; i++) {
          ctx.fillStyle = Math.random() < 0.5 ? 'rgba(255,255,255,0.18)' : 'rgba(138,90,0,0.10)';
          ctx.fillRect(Math.random() * w, Math.random() * h, 1.5, 1.5);
        }

        // Scattered paw prints
        ctx.fillStyle = 'rgba(138, 90, 0, 0.18)';
        const paws = [
          [0.12, 0.22, -0.4], [0.3, 0.75, 0.3], [0.52, 0.18, 0.2], [0.72, 0.7, -0.3],
          [0.9, 0.25, 0.5], [0.08, 0.8, 0.1], [0.92, 0.85, -0.2], [0.64, 0.42, 0.6],
        ];
        paws.forEach(([x, y, rot]) => this.paw(x * w, y * h, Math.min(w, h) * 0.09, rot));

        // Label
        ctx.save();
        ctx.fillStyle = 'rgba(255, 255, 255, 0.92)';
        const pillW = Math.min(w * 0.62, 230);
        const pillH = 46;
        const px = (w - pillW) / 2;
        const py = (h - pillH) / 2;
        ctx.beginPath();
        if (ctx.roundRect) ctx.roundRect(px, py, pillW, pillH, 23);
        else ctx.rect(px, py, pillW, pillH);
        ctx.fill();
        ctx.fillStyle = '#2F5D3A';
        ctx.font = '800 18px Outfit, "Trebuchet MS", sans-serif';
        ctx.textAlign = 'center';
        ctx.textBaseline = 'middle';
        ctx.fillText('🐾 ' + (this.dataset.coatText || 'SCRATCH ME'), w / 2, h / 2 + 1);
        ctx.restore();
      }

      paw(x, y, s, rot) {
        const ctx = this.ctx;
        ctx.save();
        ctx.translate(x, y);
        ctx.rotate(rot);
        ctx.beginPath();
        ctx.ellipse(0, s * 0.35, s * 0.55, s * 0.45, 0, 0, Math.PI * 2);
        ctx.fill();
        [[-0.55, -0.25], [-0.2, -0.6], [0.2, -0.6], [0.55, -0.25]].forEach(([dx, dy]) => {
          ctx.beginPath();
          ctx.ellipse(dx * s, dy * s, s * 0.2, s * 0.26, 0, 0, Math.PI * 2);
          ctx.fill();
        });
        ctx.restore();
      }

      scratch(from, to) {
        const ctx = this.ctx;
        ctx.globalCompositeOperation = 'destination-out';
        ctx.lineWidth = 42;
        ctx.lineCap = 'round';
        ctx.lineJoin = 'round';
        ctx.beginPath();
        ctx.moveTo(from.x, from.y);
        ctx.lineTo(to.x + 0.1, to.y + 0.1);
        ctx.stroke();
        ctx.globalCompositeOperation = 'source-over';
      }

      clearedRatio() {
        try {
          const { width, height } = this.canvas;
          const data = this.ctx.getImageData(0, 0, width, height).data;
          let clear = 0;
          let total = 0;
          for (let i = 3; i < data.length; i += 4 * 24) {
            total++;
            if (data[i] === 0) clear++;
          }
          return total ? clear / total : 0;
        } catch (e) {
          return 0;
        }
      }

      reveal() {
        if (this.revealed) return;
        this.revealed = true;
        this.classList.add('is-revealed');
        this.confetti();
        const claim = this.querySelector('[data-step="claim"]');
        claim.hidden = false;
        const input = claim.querySelector('input');
        if (input && window.matchMedia('(pointer: fine)').matches) setTimeout(() => input.focus(), 400);
      }

      confetti() {
        if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
        const canvas = this.querySelector('[data-confetti]');
        if (!canvas) return;
        const rect = this.dialog.getBoundingClientRect();
        const ratio = window.devicePixelRatio || 1;
        canvas.width = rect.width * ratio;
        canvas.height = rect.height * ratio;
        const ctx = canvas.getContext('2d');
        ctx.scale(ratio, ratio);
        const card = this.card.getBoundingClientRect();
        const ox = card.left - rect.left + card.width / 2;
        const oy = card.top - rect.top + card.height / 2;
        const colors = ['#F2B544', '#F7CE74', '#2F5D3A', '#E0694A', '#FFFFFF'];
        const bits = Array.from({ length: 90 }, () => ({
          x: ox,
          y: oy,
          vx: (Math.random() - 0.5) * 12,
          vy: -Math.random() * 11 - 3,
          s: Math.random() * 6 + 4,
          r: Math.random() * Math.PI,
          vr: (Math.random() - 0.5) * 0.3,
          c: colors[Math.floor(Math.random() * colors.length)],
          paw: Math.random() < 0.15,
        }));
        let frame = 0;
        const tick = () => {
          ctx.clearRect(0, 0, rect.width, rect.height);
          bits.forEach((b) => {
            b.vy += 0.35;
            b.vx *= 0.99;
            b.x += b.vx;
            b.y += b.vy;
            b.r += b.vr;
            ctx.save();
            ctx.translate(b.x, b.y);
            ctx.rotate(b.r);
            if (b.paw) {
              ctx.font = `${b.s * 3}px sans-serif`;
              ctx.fillText('🐾', 0, 0);
            } else {
              ctx.fillStyle = b.c;
              ctx.fillRect(-b.s / 2, -b.s / 4, b.s, b.s / 2);
            }
            ctx.restore();
          });
          if (++frame < 110) requestAnimationFrame(tick);
          else ctx.clearRect(0, 0, rect.width, rect.height);
        };
        requestAnimationFrame(tick);
      }

      async submit(e) {
        e.preventDefault();
        const form = e.currentTarget;
        const input = form.querySelector('input[type="email"]');
        const error = form.querySelector('[data-error]');
        const button = form.querySelector('button[type="submit"]');
        const email = input.value.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
          error.hidden = false;
          input.setAttribute('aria-invalid', 'true');
          input.focus();
          return;
        }
        error.hidden = true;
        input.removeAttribute('aria-invalid');
        button.disabled = true;

        const companyId = this.dataset.companyId;
        const listId = this.dataset.listId;
        if (companyId && listId) {
          try {
            await fetch(`https://a.klaviyo.com/client/subscriptions/?company_id=${encodeURIComponent(companyId)}`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/vnd.api+json', revision: '2024-10-15' },
              body: JSON.stringify({
                data: {
                  type: 'subscription',
                  attributes: {
                    custom_source: 'Scratch & Reveal popup',
                    profile: {
                      data: {
                        type: 'profile',
                        attributes: { email, properties: { nibby_signup_source: 'scratch_popup' } },
                      },
                    },
                  },
                  relationships: { list: { data: { type: 'list', id: listId } } },
                },
              }),
            });
          } catch (err) {
            /* Network error: still let the visitor continue to the offer */
          }
        }
        this.write({ subscribed: true });
        this.querySelector('[data-step="claim"]').hidden = true;
        this.querySelector('[data-step="scratch"]').hidden = true;
        this.card.hidden = true;
        const done = this.querySelector('[data-step="done"]');
        done.hidden = false;
        const link = done.querySelector('a');
        if (link) link.focus();
      }
    }
  );
}
